import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { getSubAccountList } from '@/api/auth.api';
import {
  activateSignatureV2Workflow,
  issueExternalRosterInvitation,
  cancelSignatureV2Workflow,
  confirmAndMaybeActivateSignatureV2Workflow,
  createSignatureV2EntryTicket,
  createSignatureV2WorkflowWithRoster,
  downloadSignatureV2FinalDocument,
  getSignatureV2Workflow,
  getSignatureV2Workflows,
  reopenSignatureV2Party,
  replaceSignatureV2Roster,
  resendSignatureV2Invitation,
  retrySignatureV2Workflow,
} from '@/api/signatureV2.api';
import DataTable from '@/components/DataTable';
import { Button } from '@/components/UI/Button';
import Loading from '@/components/UI/Loading';
import { Text } from '@/components/UI/Typography/Text';
import { Box, Card, Flex } from '@/layout';
import { getAuthSelector } from '@/redux/auth/auth.slice';
import { useAppSelector } from '@/redux/store.hook';
import { theme } from '@/theme/Theme';
import type {
  SignatureV2ParticipantInput,
  SignatureV2Side,
  SignatureV2Workflow,
} from '@/types/signatureV2';
import { errorHandler, successHandler } from '@/utils/toastHandler';
import { dmpFeatureFailure, DmpFeatureFailure } from '@/utils/dmpFeatureFailure';
import FeatureFailure from '@/components/DmpV2/FeatureFailure';
import { findDocumentSignatureV2Workflow } from '@/utils/signatureV2';
import { signingParticipantName } from '@/utils/signingParticipantName';
import {
  canCancelSignatureV2Workflow,
  canUseSignatureV2StepAction,
  hasSignatureV2Action,
} from '@/pages/SignatureV2/actions';
import { reserveSignatureV2EntryWindow } from '@/pages/SignatureV2/entryWindow';
import {
  formatSignatureV2SignedDate,
  getSignatureV2ApiError,
  getSignatureV2RosterConfirmationMessageKey,
  getSignatureV2WorkflowNoticeKey,
  shouldAutoActivateSignatureV2Workflow,
  shouldPollSignatureV2Workflow,
} from '@/pages/SignatureV2/workflowState';

import CancelToSignModal from './Modal';
import AddSignersModal from './Modal/AddSigners';
import {
  ActionLink,
  CompanyName,
  DetailRow,
  SignerEmail,
  ErrorText,
  ExternalPartySection,
  FileDetails,
  PartyHeading,
  PartySection,
  SignaturePanel,
  Title,
  WorkflowLayout,
} from './styles';
import { groupSignatureV2Signers, SignerRow } from './signerGroups';
import type { SignatureV2SignerCandidate } from './types';

const POLL_INTERVAL_MS = 5000;

type PendingDocument = { id: string; name: string };

interface ContractProps {
  dealId?: number;
  workflowId?: string;
  participantsData?: DealRelation[];
  documents?: DocLibrary[];
  viewerSide?: SignatureV2Side | null;
  pendingDocument?: PendingDocument | null;
  onPendingDocumentHandled?: () => void;
  onWorkflowVisibilityChange?: (visible: boolean) => void;
}

const replaceWorkflow = (workflows: SignatureV2Workflow[], updated: SignatureV2Workflow) => {
  const exists = workflows.some((workflow) => workflow.uuid === updated.uuid);
  return exists
    ? workflows.map((workflow) => (workflow.uuid === updated.uuid ? updated : workflow))
    : [...workflows, updated];
};

const ContractNew = ({
  dealId,
  workflowId,
  participantsData = [],
  documents = [],
  viewerSide = null,
  pendingDocument = null,
  onPendingDocumentHandled,
  onWorkflowVisibilityChange,
}: ContractProps) => {
  // @ts-ignore i18next's deeply nested translation key type exceeds CRA's incremental compiler limit.
  const { t: typedT } = useTranslation();
  const t = typedT as unknown as (key: string, options?: Record<string, unknown>) => string;
  const { userData } = useAppSelector(getAuthSelector);
  const [workflows, setWorkflows] = useState<SignatureV2Workflow[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [signerModalOpen, setSignerModalOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<SignatureV2Workflow | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<PendingDocument | null>(null);
  const [candidates, setCandidates] = useState<SignatureV2SignerCandidate[]>([]);
  const rosterInviteBusy = useRef(false);
  const [busyWorkflow, setBusyWorkflow] = useState<string | null>(null);
  const [signerError, setSignerError] = useState('');
  const [workflowError, setWorkflowError] = useState<Record<string, string>>({});
  const [workflowFeatureFailure, setWorkflowFeatureFailure] = useState<Record<string, DmpFeatureFailure | null>>({});
  const blockedActivation = useRef(new Set<string>());
  const [cancelWorkflow, setCancelWorkflow] = useState<SignatureV2Workflow | null>(null);
  const [cancelError, setCancelError] = useState('');
  const activationAttemptAt = useRef<Record<string, number>>({});
  const rosterScope = useRef(0);
  const workflowMutationVersion = useRef(0);
  const rosterSaveInFlight = useRef<object | null>(null);

  useEffect(() => {
    setSignerModalOpen(false);
    setSelectedWorkflow(null);
    setSelectedDocument(null);
    setSignerError('');
    setCancelWorkflow(null);
    setCancelError('');
    setWorkflowError({});
    setWorkflowFeatureFailure({});
    blockedActivation.current.clear();
    setBusyWorkflow(null);
    setWorkflows([]);
    rosterSaveInFlight.current = null;
    return () => { rosterScope.current += 1; };
  }, [dealId, workflowId]);

  const apiErrorMessage = useCallback(
    (reason: unknown): string => {
      const { code, message } = getSignatureV2ApiError(reason);
      const classified = dmpFeatureFailure(reason, userData.lang === 'en');
      if (classified.kind !== 'failed' || code === 'SIGNATURE_PROVIDER_REJECTED' || message === 'SIGNATURE_PROVIDER_REJECTED') return classified.message;
      if (code) {
        return t(`signatureV2.errors.${code}`, {
          defaultValue: message || t('signatureV2.errors.generic'),
        });
      }
      return message || t('signatureV2.errors.generic');
    },
    [t, userData.lang]
  );

  const load = useCallback(
    async (showInitialLoading = false) => {
      const scope = rosterScope.current;
      const mutationVersion = workflowMutationVersion.current;
      if (!dealId && !workflowId) {
        setInitialLoading(false);
        return;
      }
      if (showInitialLoading) setInitialLoading(true);
      try {
        const next = workflowId
          ? [await getSignatureV2Workflow(workflowId)]
          : await getSignatureV2Workflows(dealId as number);
        if (scope !== rosterScope.current || mutationVersion !== workflowMutationVersion.current) return;
        setWorkflows(next);
        setLoadError('');
      } catch (reason) {
        if (scope !== rosterScope.current || mutationVersion !== workflowMutationVersion.current) return;
        setLoadError(apiErrorMessage(reason));
      } finally {
        if (showInitialLoading && scope === rosterScope.current) setInitialLoading(false);
      }
    },
    [apiErrorMessage, dealId, workflowId]
  );

  useEffect(() => {
    void load(true);
  }, [load]);

  useEffect(() => {
    if (!workflows.some((workflow) => shouldPollSignatureV2Workflow(workflow.status))) return;
    const interval = window.setInterval(() => {
      void load(false);
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [load, workflows]);

  const loadCandidates = useCallback(async () => {
    const next: SignatureV2SignerCandidate[] = [];
    const mainEmail = typeof userData.email === 'string' ? userData.email.trim() : '';
    if (userData.id && mainEmail) {
      next.push({
        user_id: Number(userData.id),
        name: signingParticipantName(userData),
        email: mainEmail,
      });
    }

    if (userData.main_account_company_info?.isMainAccount) {
      try {
        const response = await getSubAccountList();
        const members = Array.isArray(response.data?.data) ? response.data.data : [];
        members.forEach((member: MemberProfile) => {
          const email = typeof member.email === 'string' ? member.email.trim() : '';
          if (!member.id || !email) return;
          next.push({
            user_id: Number(member.id),
            name: signingParticipantName(member),
            email,
          });
        });
      } catch {
        // The main account remains selectable even when the optional member list is unavailable.
      }
    }

    const unique = new Map<number, SignatureV2SignerCandidate>();
    next.forEach((candidate) => unique.set(candidate.user_id, candidate));
    setCandidates(Array.from(unique.values()));
  }, [userData]);

  useEffect(() => {
    if (signerModalOpen) void loadCandidates();
  }, [loadCandidates, signerModalOpen]);

  const closeSignerModal = useCallback(() => {
    setSignerModalOpen(false);
    setSelectedWorkflow(null);
    setSelectedDocument(null);
    setSignerError('');
    onPendingDocumentHandled?.();
  }, [onPendingDocumentHandled]);

  useEffect(() => {
    if (!pendingDocument || initialLoading || signerModalOpen) return;
    const existing = findDocumentSignatureV2Workflow(workflows, pendingDocument.id);
    if (existing && !hasSignatureV2Action(existing.available_actions, 'edit_own_roster')) {
      onPendingDocumentHandled?.();
      window.setTimeout(() => {
        document
          .getElementById(`signature-v2-${existing.uuid}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
      return;
    }
    if (!existing && !viewerSide) {
      setLoadError(t('signatureV2.errors.forbidden_party'));
      onPendingDocumentHandled?.();
      return;
    }
    setSelectedDocument(pendingDocument);
    setSelectedWorkflow(existing || null);
    setSignerError('');
    setSignerModalOpen(true);
  }, [initialLoading, onPendingDocumentHandled, pendingDocument, signerModalOpen, t, viewerSide, workflows]);

  const updateWorkflow = useCallback((updated: SignatureV2Workflow) => {
    // A read started before this successful mutation must not restore the previous roster.
    workflowMutationVersion.current += 1;
    setWorkflowFeatureFailure((current) => ({ ...current, [updated.uuid]: null }));
    setWorkflows((current) => replaceWorkflow(current, updated));
    setSelectedWorkflow((current) => (current?.uuid === updated.uuid ? updated : current));
  }, []);

  useEffect(() => {
    if (busyWorkflow) return;
    const readyWorkflow = workflows.find((workflow) => shouldAutoActivateSignatureV2Workflow(workflow) && !blockedActivation.current.has(`${workflow.uuid}:${workflow.version}`));
    if (!readyWorkflow) return;

    const attemptKey = `${readyWorkflow.uuid}:${readyWorkflow.version}`;
    const now = Date.now();
    if (now - (activationAttemptAt.current[attemptKey] || 0) < POLL_INTERVAL_MS) return;
    activationAttemptAt.current[attemptKey] = now;
    const scope = rosterScope.current;
    setBusyWorkflow(readyWorkflow.uuid);
    setWorkflowError((current) => ({ ...current, [readyWorkflow.uuid]: '' }));
    setWorkflowFeatureFailure((current) => ({ ...current, [readyWorkflow.uuid]: null }));

    void activateSignatureV2Workflow(readyWorkflow.uuid, readyWorkflow.version)
      .then((updated) => {
        if (scope === rosterScope.current) updateWorkflow(updated);
      })
      .catch(async (reason) => {
        if (scope !== rosterScope.current) return;
        const { code } = getSignatureV2ApiError(reason);
        if (code !== 'workflow_version_conflict' && code !== 'invalid_transition') {
          blockedActivation.current.add(attemptKey);
          const classified = dmpFeatureFailure(reason, userData.lang === 'en');
          setWorkflowFeatureFailure((current) => ({ ...current, [readyWorkflow.uuid]: classified.kind !== 'failed' ? classified : null }));
          setWorkflowError((current) => ({
            ...current,
            [readyWorkflow.uuid]: apiErrorMessage(reason),
          }));
        }
        await load(false);
      })
      .finally(() => {
        if (scope !== rosterScope.current) return;
        setBusyWorkflow((current) => (current === readyWorkflow.uuid ? null : current));
      });
  }, [apiErrorMessage, busyWorkflow, load, updateWorkflow, workflows, userData.lang]);

  const saveRoster = async (signers: SignatureV2ParticipantInput[]) => {
    if (rosterSaveInFlight.current || (!selectedWorkflow && !selectedDocument)) return;
    const request = {};
    rosterSaveInFlight.current = request;
    const scope = rosterScope.current;
    const busyKey = selectedWorkflow?.uuid || `document-${selectedDocument?.id}`;
    setBusyWorkflow(busyKey);
    setSignerError('');
    try {
      const updated = selectedWorkflow?.viewer_side
        ? await replaceSignatureV2Roster(
            selectedWorkflow.uuid,
            selectedWorkflow.viewer_side,
            selectedWorkflow.version,
            signers
          )
        : await createSignatureV2WorkflowWithRoster(Number(selectedDocument?.id), signers);
      if (scope !== rosterScope.current) return;
      updateWorkflow(updated);
      successHandler(t('signatureV2.contract.rosterSaved'));
      closeSignerModal();
    } catch (reason) {
      if (scope !== rosterScope.current) return;
      setSignerError(apiErrorMessage(reason));
      await load(false);
    } finally {
      if (rosterSaveInFlight.current === request) rosterSaveInFlight.current = null;
      if (scope === rosterScope.current) setBusyWorkflow(null);
    }
  };

  const runWorkflowMutation = async (
    workflow: SignatureV2Workflow,
    operation: (current: SignatureV2Workflow) => Promise<SignatureV2Workflow>,
    successMessage?: string
  ) => {
    const scope = rosterScope.current;
    setBusyWorkflow(workflow.uuid);
    setWorkflowError((current) => ({ ...current, [workflow.uuid]: '' }));
    try {
      const updated = await operation(workflow);
      if (scope !== rosterScope.current) return null;
      updateWorkflow(updated);
      if (successMessage) successHandler(successMessage);
      return updated;
    } catch (reason) {
      if (scope !== rosterScope.current) return null;
      const message = apiErrorMessage(reason);
      const classified = dmpFeatureFailure(reason, userData.lang === 'en');
      if (classified.kind !== 'failed') blockedActivation.current.add(`${workflow.uuid}:${workflow.version}`);
      setWorkflowFeatureFailure((current) => ({ ...current, [workflow.uuid]: classified.kind !== 'failed' ? classified : null }));
      setWorkflowError((current) => ({ ...current, [workflow.uuid]: message }));
      errorHandler(message);
      await load(false);
      return null;
    } finally {
      if (scope === rosterScope.current) setBusyWorkflow(null);
    }
  };

  const reopenAndEdit = async (workflow: SignatureV2Workflow) => {
    if (!workflow.viewer_side) return;
    const updated = await runWorkflowMutation(workflow, (current) =>
      reopenSignatureV2Party(current.uuid, current.viewer_side as SignatureV2Side, current.version)
    );
    if (!updated) return;
    setSelectedWorkflow(updated);
    setSelectedDocument({ id: String(updated.source_document.id), name: updated.source_document.name });
    setSignerModalOpen(true);
  };

  const openRoster = (workflow: SignatureV2Workflow) => {
    if (rosterSaveInFlight.current) return;
    setSelectedWorkflow(workflow);
    setSelectedDocument({ id: String(workflow.source_document.id), name: workflow.source_document.name });
    setSignerError('');
    setSignerModalOpen(true);
  };

  const confirmRoster = async (workflow: SignatureV2Workflow) => {
    if (!workflow.viewer_side) return;
    const updated = await runWorkflowMutation(workflow, confirmAndMaybeActivateSignatureV2Workflow);
    if (updated) successHandler(t(getSignatureV2RosterConfirmationMessageKey(updated.status)));
  };

  const openSigningEntry = async (workflow: SignatureV2Workflow, stepId: number) => {
    const entryWindow = reserveSignatureV2EntryWindow(window.open.bind(window));
    if (!entryWindow) {
      const message = t('signatureV2.errors.popup_blocked');
      setWorkflowError((current) => ({ ...current, [workflow.uuid]: message }));
      errorHandler(message);
      return;
    }

    setBusyWorkflow(workflow.uuid);
    try {
      const ticket = await createSignatureV2EntryTicket(workflow.uuid, stepId, workflow.version);
      entryWindow.location.replace(ticket.entry_url);
    } catch (reason) {
      entryWindow.close();
      const message = apiErrorMessage(reason);
      setWorkflowError((current) => ({ ...current, [workflow.uuid]: message }));
      errorHandler(message);
    } finally {
      setBusyWorkflow(null);
    }
  };

  const downloadFinal = async (workflow: SignatureV2Workflow) => {
    setBusyWorkflow(workflow.uuid);
    try {
      const blob = await downloadSignatureV2FinalDocument(workflow.uuid);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = workflow.final_document?.name || workflow.source_document.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (reason) {
      const message = apiErrorMessage(reason);
      setWorkflowError((current) => ({ ...current, [workflow.uuid]: message }));
      errorHandler(message);
    } finally {
      setBusyWorkflow(null);
    }
  };

  const submitCancel = async (reason: string) => {
    if (!cancelWorkflow) return;
    const scope = rosterScope.current;
    setBusyWorkflow(cancelWorkflow.uuid);
    setCancelError('');
    try {
      const updated = await cancelSignatureV2Workflow(cancelWorkflow.uuid, cancelWorkflow.version, reason);
      if (scope !== rosterScope.current) return;
      updateWorkflow(updated);
      setCancelWorkflow(null);
      successHandler(t('signatureV2.contract.cancelled'));
    } catch (cause) {
      if (scope !== rosterScope.current) return;
      setCancelError(apiErrorMessage(cause));
      await load(false);
    } finally {
      if (scope === rosterScope.current) setBusyWorkflow(null);
    }
  };

  const columns: ColumnDef<SignerRow>[] = [
    {
      accessorKey: 'participant.name',
      header: t('signatureV2.contract.columns.name'),
      cell: ({ row }) => `${row.original.participant.position}. ${signingParticipantName(row.original.participant)}`,
    },
    {
      accessorKey: 'participant.email',
      header: t('signatureV2.emailLabel'),
      cell: ({ row }) => <SignerEmail>{row.original.participant.email || '-'}</SignerEmail>,
    },
    {
      accessorKey: 'date',
      header: t('signatureV2.contract.columns.dateSigned'),
      cell: ({ row }) => formatSignatureV2SignedDate(row.original.step?.signed_at),
    },
    {
      accessorKey: 'action',
      header: t('signatureV2.contract.columns.action'),
      cell: ({ row }) => {
        const { workflow, step } = row.original;
        const busy = busyWorkflow === workflow.uuid;
        const canOpen = canUseSignatureV2StepAction(workflow, step, 'open_signing_entry');
        const canResend = canUseSignatureV2StepAction(workflow, step, 'resend_current_invitation');

        return (
          <Flex gridGap={10} alignItems="center" justifyContent="flex-end">
            {canResend && step && (
              <ActionLink
                type="button"
                disabled={busy}
                onClick={() =>
                  void runWorkflowMutation(
                    workflow,
                    (current) => resendSignatureV2Invitation(current.uuid, step.id, current.version),
                    t('signatureV2.contract.invitationResent')
                  )
                }
              >
                {t('signatureV2.actions.resend')}
              </ActionLink>
            )}
            {step?.status === 'signed' ? (
              <Button type="button" size="table" variant="regular" special="fake" style={{ minWidth: 120 }}>
                {t('buttons.signed')}
              </Button>
            ) : canOpen && step ? (
              <Button
                type="button"
                size="table"
                variant="yellow"
                disabled={busy}
                style={{ color: '#000', minWidth: 120 }}
                onClick={() => void openSigningEntry(workflow, step.id)}
              >
                {t('buttons.clickToSign')}
              </Button>
            ) : (
              <Button type="button" size="table" variant="normal" special="fake" style={{ minWidth: 120 }}>
                {t('buttons.waiting')}
              </Button>
            )}
          </Flex>
        );
      },
    },
  ];

  const externalColumns: ColumnDef<SignerRow>[] = [
    {
      accessorKey: 'participant.name',
      header: t('signatureV2.contract.columns.name'),
      cell: ({ row }) => (
        <Flex flexDirection="column">
          <span>{signingParticipantName(row.original.participant)}</span>
          <Text color={theme.colors.gray500} fontSize="sm">
            {`${t(`signatureV2.side.${row.original.partySide}`)} · ${t('signatureV2.contract.signingOrder')} ${row.original.step?.sequence ?? row.original.participant.position}`}
          </Text>
        </Flex>
      ),
    },
    ...columns.slice(1),
  ];

  const activeWorkflows = workflows.filter((workflow) => workflow.status !== 'cancelled');
  const hasVisibleWorkflow = activeWorkflows.length > 0;
  const modalSide = selectedWorkflow?.viewer_side || viewerSide || 'buyer';
  const modalParty = selectedWorkflow?.parties.find((party) => party.side === modalSide);
  const fallbackCompany =
    participantsData.find((participant) => participant.tag?.toLowerCase() === modalSide)?.name || '';
  const initialSigners = useMemo(
    () => [...(modalParty?.participants || [])]
      .sort((first, second) => first.position - second.position)
      .map(({ user_id, name, email }) => ({ user_id, name, email })),
    [modalParty]
  );
  const otherPartyEmails = useMemo(
    () => selectedWorkflow?.parties
      .filter((party) => party.side !== modalSide)
      .flatMap((party) => party.participants.map((participant) => participant.email)) || [],
    [modalSide, selectedWorkflow]
  );

  useEffect(() => {
    if (!initialLoading) onWorkflowVisibilityChange?.(hasVisibleWorkflow);
  }, [hasVisibleWorkflow, initialLoading, onWorkflowVisibilityChange]);

  if (initialLoading && workflowId) {
    return (
      <Card>
        <Flex justifyContent="center">
          <Loading />
        </Flex>
      </Card>
    );
  }

  return (
    <>
      <AddSignersModal
        showModal={signerModalOpen}
        side={modalSide}
        companyName={modalParty?.display_name || fallbackCompany}
        candidates={candidates}
        initialSigners={initialSigners}
        otherPartyEmails={otherPartyEmails}
        busy={busyWorkflow !== null}
        apiError={signerError}
        setShowModal={(show) => (show ? setSignerModalOpen(true) : closeSignerModal())}
        onSubmit={saveRoster}
      />
      <CancelToSignModal
        showModal={Boolean(cancelWorkflow)}
        busy={Boolean(cancelWorkflow && busyWorkflow === cancelWorkflow.uuid)}
        apiError={cancelError}
        setShowModal={(show) => {
          if (!show) {
            setCancelWorkflow(null);
            setCancelError('');
          }
        }}
        onSubmit={submitCancel}
      />

      {(activeWorkflows.length > 0 || loadError) && (
        <Card id="signature-v2-contracts">
          <Text fontWeight={900} marginBottom={10}>
            {t('pages.dealDetail.contract.title')}
          </Text>
          <Text marginBottom={30}>{t('pages.dealDetail.contract.description')}</Text>
          {loadError && <ErrorText role="alert">{loadError}</ErrorText>}

          {activeWorkflows.map((workflow) => {
            const signerGroups = groupSignatureV2Signers(workflow);
            const source = documents.find((document) => String(document.id) === String(workflow.source_document.id));
            const uploader = participantsData.find((participant) => String(participant.id) === String(source?.acc_id));
            const uploadDate = source?.file?.updated_at ? new Date(source.file.updated_at).toLocaleDateString() : '-';
            const isBusy = busyWorkflow === workflow.uuid;
            const stateError = workflow.failure_code
              ? t(`signatureV2.errors.${workflow.failure_code}`, {
                  defaultValue: t('signatureV2.errors.generic'),
                })
              : '';
            const noticeKey = workflowFeatureFailure[workflow.uuid] ? null : getSignatureV2WorkflowNoticeKey(workflow.status);

            return (
              <WorkflowLayout key={workflow.uuid} id={`signature-v2-${workflow.uuid}`}>
                <Box width="100%">
                  <Title>
                    <Text>{t('signatureV2.contract.signFile')}</Text>
                  </Title>
                  <FileDetails>
                    <DetailRow>
                      <Text fontWeight={900} color={theme.colors.primary500}>
                        {t('signatureV2.contract.contract')}
                      </Text>
                      <Text>{workflow.source_document.name}</Text>
                    </DetailRow>
                    <DetailRow>
                      <Text fontWeight={900} color={theme.colors.primary500}>
                        {t('signatureV2.contract.uploadedBy')}
                      </Text>
                      <Text>{uploader?.name || '-'}</Text>
                    </DetailRow>
                    <DetailRow>
                      <Text fontWeight={900} color={theme.colors.primary500}>
                        {t('signatureV2.contract.uploadDate')}
                      </Text>
                      <Text>{uploadDate}</Text>
                    </DetailRow>

                    {noticeKey && <Text>{t(noticeKey)}</Text>}

                    {hasSignatureV2Action(workflow.available_actions, 'invite_external_buyer') && (
                      <Button type="button" disabled={isBusy} onClick={async () => {
                        if (busyWorkflow || rosterInviteBusy.current) return;
                        rosterInviteBusy.current = true; setBusyWorkflow(workflow.uuid);
                        try { await issueExternalRosterInvitation(workflow.uuid); successHandler(t('signatureV2.externalRoster.queued')); }
                        catch { errorHandler(t('signatureV2.errors.generic')); }
                        finally { rosterInviteBusy.current = false; setBusyWorkflow(null); }
                      }}>{t('signatureV2.externalRoster.resend')}</Button>
                    )}
                    {hasSignatureV2Action(workflow.available_actions, 'retry') && (
                      <Flex justifyContent="center">
                        <Button
                          type="button"
                          variant="primary"
                          disabled={isBusy}
                          onClick={() =>
                            void runWorkflowMutation(workflow, (current) =>
                              retrySignatureV2Workflow(current.uuid, current.version)
                            )
                          }
                        >
                          {t('signatureV2.actions.retry')}
                        </Button>
                      </Flex>
                    )}
                    {hasSignatureV2Action(workflow.available_actions, 'download_final') && (
                      <Flex justifyContent="center">
                        <Button
                          type="button"
                          variant="primary"
                          disabled={isBusy}
                          onClick={() => void downloadFinal(workflow)}
                        >
                          {t('signatureV2.actions.downloadFinal')}
                        </Button>
                      </Flex>
                    )}
                    {canCancelSignatureV2Workflow(workflow) && (
                      <Flex justifyContent="center">
                        <ActionLink
                          type="button"
                          disabled={isBusy}
                          onClick={() => {
                            setCancelError('');
                            setCancelWorkflow(workflow);
                          }}
                        >
                          {t('signatureV2.actions.cancel')}
                        </ActionLink>
                      </Flex>
                    )}
                    {workflowFeatureFailure[workflow.uuid] && <>
                      <FeatureFailure failure={workflowFeatureFailure[workflow.uuid]} english={userData.lang === 'en'} />
                      <Button type="button" variant="primary-invert" onClick={() => {
                        if (workflowFeatureFailure[workflow.uuid]?.kind !== 'reconciliation') {
                          blockedActivation.current.delete(`${workflow.uuid}:${workflow.version}`);
                          activationAttemptAt.current[`${workflow.uuid}:${workflow.version}`] = 0;
                        }
                        void load(false);
                      }}>{userData.lang === 'en' ? 'Refresh status' : '重新整理狀態'}</Button>
                    </>}
                    {!workflowFeatureFailure[workflow.uuid] && (workflowError[workflow.uuid] || stateError) && (
                      <ErrorText role="alert">{workflowError[workflow.uuid] || stateError}</ErrorText>
                    )}
                  </FileDetails>
                </Box>

                <SignaturePanel>
                  <Title>
                    <Text>{t('info.esigns')}</Text>
                  </Title>
                  {workflow.parties.map((party) => {
                    const rows = signerGroups.platform[party.side];
                    const isOwnParty = party.side === workflow.viewer_side;

                    return (
                      <PartySection key={party.side} aria-label={t(`signatureV2.side.${party.side}`)}>
                        <PartyHeading>
                          <Text fontWeight={900} color={theme.colors.primary500}>
                            {t(`signatureV2.side.${party.side}`)}
                          </Text>
                          <CompanyName title={party.display_name || ''}>{party.display_name || '-'}</CompanyName>
                        </PartyHeading>
                        {party.status === 'confirmed' && workflow.status === 'configuring' && (
                          <Text color={theme.colors.primary500}>{t('signatureV2.partyStatus.confirmed')}</Text>
                        )}
                        {rows.length > 0 && <DataTable hidePagination mobileCardLayout columns={columns} data={rows} />}
                        {isOwnParty && hasSignatureV2Action(workflow.available_actions, 'edit_own_roster') && (
                          <Flex flexWrap="wrap" gridGap={12}>
                            <Button
                              type="button"
                              variant="primary"
                              disabled={isBusy}
                              onClick={() => openRoster(workflow)}
                            >
                              {t(
                                party.participants.length > 0 ? 'signatureV2.contract.editSigners' : 'signatureV2.contract.addSigners'
                              )}
                            </Button>
                            {hasSignatureV2Action(workflow.available_actions, 'confirm_own_party') && (
                              <Button
                                type="button"
                                variant="primary"
                                disabled={isBusy}
                                onClick={() => void confirmRoster(workflow)}
                              >
                                {t('signatureV2.actions.confirmRoster')}
                              </Button>
                            )}
                          </Flex>
                        )}
                        {isOwnParty && hasSignatureV2Action(workflow.available_actions, 'reopen_own_party') && (
                          <Flex>
                            <Button
                              type="button"
                              variant="primary"
                              disabled={isBusy}
                              onClick={() => void reopenAndEdit(workflow)}
                            >
                              {t('signatureV2.contract.editSigners')}
                            </Button>
                          </Flex>
                        )}
                      </PartySection>
                    );
                  })}
                  {signerGroups.external.length > 0 && (
                    <ExternalPartySection aria-label={t('signatureV2.contract.thirdPartySigners')}>
                      <Text fontWeight={900} color={theme.colors.primary500}>
                        {t('signatureV2.contract.thirdPartySigners')}
                      </Text>
                      <DataTable hidePagination mobileCardLayout columns={externalColumns} data={signerGroups.external} />
                    </ExternalPartySection>
                  )}
                </SignaturePanel>
              </WorkflowLayout>
            );
          })}
        </Card>
      )}
    </>
  );
};

export default ContractNew;
